import { SANDBOX_MAGIC, type AdvanceClockResponseSchema, DEMO_ENTITY } from '@paguz/contracts';
import {
  AmountInput,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatCop,
  Input,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  CalendarClockIcon,
  CheckCircle2Icon,
  CircleXIcon,
  DatabaseBackupIcon,
  ExternalLinkIcon,
  ListChecksIcon,
  Maximize2Icon,
  QrCodeIcon,
  RotateCcwIcon,
  Undo2Icon,
  ZapIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { z } from 'zod';
import type { DemoResetResult } from '../../api/admin-schemas.ts';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog, SummaryRows } from '../../components/confirm-dialog.tsx';
import { Field, Select, SuffixInput, compactInputClass } from '../../components/form-controls.tsx';
import { CopyValue, ErrorState, PageHeader, Panel } from '../../components/page.tsx';
import { formatBusinessDate, formatDateTime, formatLongDate } from '../../lib/format.ts';
import { FAULT_MODE_ORDER, FAULT_MODES, type FaultMode } from './fault-modes.ts';

/** Controles de demo (`06-backoffice.md` §4.13, solo `DEMO_MODE=true`). */
export function DemoPage() {
  return (
    <>
      <PageHeader
        title="Controles de demo"
        description={`Reloj simulado, fallas de Bre-B, QR de comercios y reset de datos para ensayar el guion con ${DEMO_ENTITY.name}. Cada acción queda en auditoría.`}
        actions={<Badge variant="warning">DEMO_MODE</Badge>}
      />
      <div className="grid gap-6 xl:grid-cols-2">
        <ClockPanel />
        <FaultPanel />
        <QrPanel />
        <div className="flex flex-col gap-6">
          <ResetPanel />
          <RefundPanel />
        </div>
        <GuidePanel />
      </div>
    </>
  );
}

// ── Reloj ─────────────────────────────────────────────────────────────────
type AdvanceResult = z.infer<typeof AdvanceClockResponseSchema>;

function ClockPanel() {
  const queryClient = useQueryClient();
  const clock = useQuery({ queryKey: queryKeys.clock, queryFn: api.getClock });
  const [days, setDays] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<AdvanceResult | null>(null);

  const refreshAll = () => {
    void queryClient.invalidateQueries();
  };
  const advance = useMutation({
    mutationFn: () => api.advanceClock(days),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.clock, data.clock);
      setResult(data);
      setConfirming(false);
      refreshAll();
      toast.success(`Cierre diario ejecutado para ${data.days.length} días`);
    },
  });
  const reset = useMutation({
    mutationFn: api.resetClock,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.clock, data);
      setResult(null);
      refreshAll();
      toast.success('Reloj en tiempo real');
    },
  });

  const totals = result?.days.reduce(
    (acc, d) => ({
      statements: acc.statements + d.statementsGenerated,
      delinquency: acc.delinquency + d.delinquencyChanges,
    }),
    { statements: 0, delinquency: 0 },
  );
  const validDays = Number.isInteger(days) && days >= 1 && days <= 90;

  return (
    <Panel
      id="reloj"
      className="scroll-mt-24 xl:col-span-2"
      title={
        <span className="flex items-center gap-2">
          <CalendarClockIcon className="size-5 text-highlight" /> Reloj de demo
        </span>
      }
      description="Avanzar N días ejecuta el cierre diario por cada día: causación de intereses, cortes de extracto, mora y recordatorios."
      actions={
        clock.data?.mode === 'SIMULATED' && (
          <Button
            variant="outline"
            size="sm"
            loading={reset.isPending}
            onClick={() => {
              reset.mutate();
            }}
          >
            <RotateCcwIcon /> Volver a tiempo real
          </Button>
        )
      }
    >
      {clock.isError ? (
        <ErrorState error={clock.error} onRetry={() => void clock.refetch()} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="rounded-2xl bg-inverse p-6 text-inverse-foreground">
            <p className="text-sm text-inverse-muted-foreground">Fecha y hora del sistema</p>
            <p className="mt-2 font-display text-3xl leading-tight font-semibold first-letter:uppercase">
              {clock.data ? formatLongDate(clock.data.now) : '…'}
            </p>
            <p className="mt-1 text-inverse-foreground/85 tabular-nums">
              {clock.data ? formatDateTime(clock.data.now) : ''}
            </p>
            <div className="mt-4">
              {clock.data?.mode === 'SIMULATED' ? (
                <Badge variant="warning">Simulado · +{clock.data.offsetDays} días</Badge>
              ) : (
                <Badge variant="success">Tiempo real</Badge>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-2 text-sm font-semibold">Avanzar</p>
              <div className="flex flex-wrap gap-2">
                {[1, 7, 15, 30].map((n) => (
                  <Button
                    key={n}
                    variant={days === n ? 'secondary' : 'outline'}
                    className={cn('h-10', days === n && 'ring-2 ring-highlight')}
                    onClick={() => {
                      setDays(n);
                    }}
                  >
                    +{n} {n === 1 ? 'día' : 'días'}
                  </Button>
                ))}
                <SuffixInput
                  aria-label="Días a avanzar"
                  suffix="días"
                  className="w-32"
                  inputMode="numeric"
                  value={Number.isNaN(days) ? '' : String(days)}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    setDays(v === '' ? Number.NaN : Number.parseInt(v, 10));
                  }}
                />
              </div>
              {!validDays && <p className="mt-1 text-sm text-destructive">Entre 1 y 90 días.</p>}
            </div>
            <Button
              variant="highlight"
              size="lg"
              disabled={!validDays}
              onClick={() => {
                setConfirming(true);
              }}
            >
              <CalendarClockIcon /> Avanzar y correr cierre
            </Button>
          </div>
        </div>
      )}

      {result && totals && (
        <div className="mt-6">
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-success-soft px-4 py-3 text-sm text-success-soft-foreground">
            <CheckCircle2Icon className="size-5" />
            <span>
              <strong>Cierre diario ejecutado</strong> para {result.days.length} días ·{' '}
              {totals.statements} extractos generados · {totals.delinquency} cambios de mora.
            </span>
          </div>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-border">
            <Table>
              <TableHeader className="sticky top-0">
                <TableRow>
                  <TableHead>Fecha de negocio</TableHead>
                  <TableHead className="text-right">Intereses causados</TableHead>
                  <TableHead className="text-center">Extractos</TableHead>
                  <TableHead className="text-center">Cambios de mora</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.days.map((d) => (
                  <TableRow
                    key={d.businessDate}
                    className={cn(d.statementsGenerated > 0 && 'bg-highlight-soft/40')}
                  >
                    <TableCell>{formatBusinessDate(d.businessDate)}</TableCell>
                    <TableCell className="text-right">
                      <MoneyText value={d.interestAccrued.amount} size="sm" />
                    </TableCell>
                    <TableCell className="text-center">
                      {d.statementsGenerated > 0 ? (
                        <Badge variant="highlight">{d.statementsGenerated}</Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {d.delinquencyChanges > 0 ? (
                        <Badge variant="warning">{d.delinquencyChanges}</Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog
        open={confirming}
        onOpenChange={(open) => {
          if (!advance.isPending) setConfirming(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              ¿Avanzar {days} {days === 1 ? 'día' : 'días'}?
            </DialogTitle>
            <DialogDescription>
              Se ejecuta el cierre diario por cada día: intereses, cortes de extracto, mora y
              recordatorios. Los datos cambian como si el tiempo hubiera pasado; se revierte con
              «Reset de datos».
            </DialogDescription>
          </DialogHeader>
          {advance.isError && (
            <p
              role="alert"
              className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-foreground"
            >
              {describeError(advance.error)}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={advance.isPending}
              onClick={() => {
                setConfirming(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="highlight"
              loading={advance.isPending}
              onClick={() => {
                advance.mutate();
              }}
            >
              Avanzar {days} {days === 1 ? 'día' : 'días'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ── Fallas Bre-B ──────────────────────────────────────────────────────────
function FaultPanel() {
  const queryClient = useQueryClient();
  const state = useQuery({ queryKey: queryKeys.faultMode, queryFn: api.getFaultMode });
  const [mode, setMode] = useState<FaultMode>('TIMEOUT');
  const [count, setCount] = useState(1);
  const [latency, setLatency] = useState('');
  const apply = useMutation({
    mutationFn: (body: { mode: FaultMode; count: number; latencyMs?: number }) =>
      api.setFaultMode(body),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.faultMode, data);
      toast.success(
        data.mode === 'NORMAL'
          ? 'Bre-B en modo normal'
          : `${FAULT_MODES[data.mode].label} para las próximas ${data.remaining}`,
      );
    },
    onError: (error) => toast.error(describeError(error)),
  });
  const current =
    state.data?.mode !== 'NORMAL' && (state.data?.remaining ?? 0) > 0 ? state.data : null;
  const active = current !== null;

  return (
    <Panel
      id="fallas"
      className="scroll-mt-24"
      title={
        <span className="flex items-center gap-2">
          <ZapIcon className="size-5 text-highlight" /> Fallas Bre-B
        </span>
      }
      description="Fuerza el comportamiento del simulador para las próximas N transacciones."
    >
      <div
        className={cn(
          'mb-4 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm',
          active
            ? 'bg-warning-soft text-warning-soft-foreground'
            : 'bg-success-soft text-success-soft-foreground',
        )}
        role="status"
      >
        <span>
          {current ? (
            <>
              <strong>{FAULT_MODES[current.mode].label}</strong> activo para las próximas{' '}
              <strong>{current.remaining}</strong> transacciones
              {current.latencyMs !== null && ` · latencia ${current.latencyMs} ms`}
            </>
          ) : (
            <>
              <strong>Normal</strong>: Bre-B confirma en 1–3 s.
            </>
          )}
        </span>
        {active && (
          <Button
            size="sm"
            variant="outline"
            loading={apply.isPending}
            onClick={() => {
              apply.mutate({ mode: 'NORMAL', count: 1 });
            }}
          >
            Volver a normal
          </Button>
        )}
      </div>

      <div role="radiogroup" aria-label="Modo de falla" className="grid gap-2 sm:grid-cols-2">
        {FAULT_MODE_ORDER.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            onClick={() => {
              setMode(m);
            }}
            className={cn(
              'cursor-pointer rounded-xl border-2 px-3 py-2.5 text-left transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none',
              mode === m
                ? 'border-highlight bg-highlight-soft/40'
                : 'border-border hover:border-highlight/40',
            )}
          >
            <span className="block text-sm font-semibold">{FAULT_MODES[m].label}</span>
            <span className="block text-xs text-muted-foreground">{FAULT_MODES[m].effect}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <Field label="Próximas" htmlFor="fault-count">
          <SuffixInput
            id="fault-count"
            suffix="transacciones"
            inputMode="numeric"
            value={String(count)}
            onChange={(e) => {
              setCount(
                Math.max(
                  1,
                  Math.min(100, Number.parseInt(e.target.value.replace(/\D/g, '') || '1', 10)),
                ),
              );
            }}
          />
        </Field>
        <Field label="Latencia simulada (opcional)" htmlFor="fault-latency">
          <SuffixInput
            id="fault-latency"
            suffix="ms"
            inputMode="numeric"
            placeholder="1000–3000"
            value={latency}
            onChange={(e) => {
              setLatency(e.target.value.replace(/\D/g, ''));
            }}
          />
        </Field>
        <Button
          variant="highlight"
          className="h-10"
          loading={apply.isPending}
          onClick={() => {
            apply.mutate({
              mode,
              count,
              ...(latency !== '' && { latencyMs: Number.parseInt(latency, 10) }),
            });
          }}
        >
          Aplicar
        </Button>
      </div>

      <details className="mt-5 rounded-xl border border-border px-4 py-3 text-sm">
        <summary className="cursor-pointer font-semibold">
          Convenciones por monto (sin tocar los controles)
        </summary>
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {Object.entries(SANDBOX_MAGIC.breb.amountCents).map(([cents, effect]) => (
            <li key={cents}>
              <code className="font-mono">$ x.{cents}</code> →{' '}
              <span className="text-muted-foreground">{effect}</span>
            </li>
          ))}
          <li>
            <code className="font-mono">
              $ x.{SANDBOX_MAGIC.depositCore.creditAndHoldFailCents}
            </code>{' '}
            → <span className="text-muted-foreground">falla el fondeo JIT (compensación)</span>
          </li>
          <li>
            Llaves inexistentes:{' '}
            <code className="font-mono">{SANDBOX_MAGIC.breb.unknownAliases.join(', ')}</code>
          </li>
        </ul>
      </details>
    </Panel>
  );
}

// ── QR ────────────────────────────────────────────────────────────────────
function QrPanel() {
  const merchants = useQuery({ queryKey: queryKeys.merchants, queryFn: api.listDemoMerchants });
  const [alias, setAlias] = useState('');
  const [amount, setAmount] = useState('180000');
  const [reference, setReference] = useState('');
  const [zoom, setZoom] = useState(false);
  const selected = alias !== '' ? alias : (merchants.data?.data[0]?.alias ?? '');
  const merchant = merchants.data?.data.find((m) => m.alias === selected);
  const qr = useMutation({
    mutationFn: () =>
      api.generateQr({
        merchantAlias: selected,
        ...(amount !== '' &&
          amount !== '0' && { amount: { amount: `${amount}.00`, currency: 'COP' } }),
        ...(reference.trim() !== '' && { reference: reference.trim() }),
      }),
    onError: (error) => toast.error(describeError(error)),
  });

  return (
    <Panel
      id="qr"
      className="scroll-mt-24"
      title={
        <span className="flex items-center gap-2">
          <QrCodeIcon className="size-5 text-highlight" /> Generar QR de comercio
        </span>
      }
      description="Sin monto: QR estático. Con monto: QR dinámico para escanear desde el celular."
    >
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_14rem]">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            qr.mutate();
          }}
        >
          <Field label="Comercio" htmlFor="qr-merchant">
            <Select
              id="qr-merchant"
              value={selected}
              onChange={(e) => {
                setAlias(e.target.value);
              }}
            >
              {merchants.data?.data.map((m) => (
                <option key={m.alias} value={m.alias}>
                  {m.name} · {m.aliasLabel}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Monto (opcional)"
            htmlFor="qr-amount"
            hint={
              amount === '' || amount === '0'
                ? 'QR estático: el cliente escribe el monto.'
                : 'QR dinámico.'
            }
          >
            <AmountInput
              id="qr-amount"
              className="h-10 text-sm"
              value={amount}
              onValueChange={setAmount}
            />
          </Field>
          <Field label="Referencia (opcional)" htmlFor="qr-ref">
            <Input
              id="qr-ref"
              className={compactInputClass}
              maxLength={25}
              value={reference}
              placeholder="FDP-2026-000124"
              onChange={(e) => {
                setReference(e.target.value);
              }}
            />
          </Field>
          <Button type="submit" variant="highlight" loading={qr.isPending} disabled={!selected}>
            <QrCodeIcon /> Generar QR
          </Button>
        </form>
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border p-3 text-center">
          {qr.data ? (
            <>
              <button
                type="button"
                className="group relative cursor-zoom-in"
                aria-label="Ampliar QR"
                onClick={() => {
                  setZoom(true);
                }}
              >
                <img
                  src={qr.data.pngDataUrl}
                  alt={`QR de ${merchant?.name ?? 'comercio'}`}
                  className="size-48 rounded-lg bg-white"
                />
                <Maximize2Icon className="absolute right-1 bottom-1 size-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
              </button>
              <p className="text-xs font-semibold">{merchant?.name}</p>
              {amount !== '' && amount !== '0' && <MoneyText value={amount} size="sm" />}
              <CopyValue value={qr.data.payload} label="payload del QR" display="payload" />
            </>
          ) : (
            <p className="px-2 text-sm text-muted-foreground">El QR aparece aquí.</p>
          )}
        </div>
      </div>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{merchant?.name}</DialogTitle>
            <DialogDescription>
              {amount !== '' && amount !== '0'
                ? `QR dinámico por ${formatCop(amount)}`
                : 'QR estático'}
            </DialogDescription>
          </DialogHeader>
          {qr.data && (
            <img
              src={qr.data.pngDataUrl}
              alt="QR ampliado"
              className="mx-auto w-full max-w-md rounded-xl bg-white"
            />
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ── Reset ─────────────────────────────────────────────────────────────────
function ResetPanel() {
  const queryClient = useQueryClient();
  const customers = useQuery({
    queryKey: queryKeys.customers(''),
    queryFn: () => api.listCustomers(''),
  });
  const [scope, setScope] = useState<'ALL' | 'CUSTOMER'>('ALL');
  const [customerId, setCustomerId] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<DemoResetResult | null>(null);
  const selectedCustomer = customerId !== '' ? customerId : (customers.data?.data[0]?.id ?? '');
  const customerName = customers.data?.data.find((c) => c.id === selectedCustomer)?.fullName;

  return (
    <Panel
      id="reset"
      className="scroll-mt-24"
      title={
        <span className="flex items-center gap-2">
          <DatabaseBackupIcon className="size-5 text-highlight" /> Reset de datos
        </span>
      }
      description="Restaura la semilla del demo. Úsalo antes de cada ensayo (T−30 min)."
    >
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label="Alcance del reset" className="grid gap-2 sm:grid-cols-2">
          {(
            [
              ['ALL', 'Todo el demo', 'Clientes, líneas, compras, reloj y fallas'],
              ['CUSTOMER', 'Un cliente de prueba', 'Solo sus datos'],
            ] as const
          ).map(([value, label, hint]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={scope === value}
              onClick={() => {
                setScope(value);
              }}
              className={cn(
                'cursor-pointer rounded-xl border-2 px-3 py-2.5 text-left focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none',
                scope === value
                  ? 'border-highlight bg-highlight-soft/40'
                  : 'border-border hover:border-highlight/40',
              )}
            >
              <span className="block text-sm font-semibold">{label}</span>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </button>
          ))}
        </div>
        {scope === 'CUSTOMER' && (
          <Field label="Cliente" htmlFor="reset-customer">
            <Select
              id="reset-customer"
              value={selectedCustomer}
              onChange={(e) => {
                setCustomerId(e.target.value);
              }}
            >
              {customers.data?.data.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.fullName}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Button
          variant="destructive"
          onClick={() => {
            setOpen(true);
          }}
        >
          <DatabaseBackupIcon /> Restaurar semilla
        </Button>
        {result && (
          <ul
            className="flex flex-col gap-1.5 rounded-xl border border-border p-4 text-sm"
            aria-label="Verificación de la semilla"
          >
            <li className="mb-1 text-xs text-muted-foreground">
              Restaurado {formatDateTime(result.resetAt)}
            </li>
            {result.checks.map((check) => (
              <li key={check.label} className="flex items-center gap-2">
                {check.ok ? (
                  <CheckCircle2Icon className="size-4 text-success" />
                ) : (
                  <CircleXIcon className="size-4 text-destructive" />
                )}
                {check.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      <ConfirmActionDialog
        open={open}
        onOpenChange={setOpen}
        title="Restaurar la semilla del demo"
        description="Se pierden las compras, cambios de configuración y avances de reloj hechos desde el último reset. La auditoría se conserva."
        destructive
        confirmWord="RESTAURAR"
        requireReason={false}
        confirmLabel="Restaurar semilla"
        summary={
          <SummaryRows
            rows={[
              ['Alcance', scope === 'ALL' ? 'Todo el demo' : `Cliente: ${customerName ?? ''}`],
            ]}
          />
        }
        onConfirm={async () => {
          const data = await api.resetDemo(
            scope === 'ALL' ? { scope } : { scope, customerId: selectedCustomer },
          );
          setResult(data);
          void queryClient.invalidateQueries();
          toast.success('Semilla restaurada');
        }}
      />
    </Panel>
  );
}

// ── Devolución simulada (P1) ──────────────────────────────────────────────
function RefundPanel() {
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [amount, setAmount] = useState('');
  const refund = useMutation({
    mutationFn: () =>
      api.merchantRefund({
        paymentIntentId: paymentIntentId.trim(),
        amount: { amount: `${amount}.00`, currency: 'COP' },
      }),
    onSuccess: () => toast.success('El comercio envió la devolución por Bre-B (entrante).'),
    onError: (error) => toast.error(describeError(error)),
  });
  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <Undo2Icon className="size-5 text-highlight" /> Simular devolución del comercio
        </span>
      }
      description="El sandbox emite una transferencia entrante asociada a la compra."
    >
      <form
        className="grid items-end gap-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          refund.mutate();
        }}
      >
        <Field label="Compra (pi_…)" htmlFor="refund-pi">
          <Input
            id="refund-pi"
            className={cn(compactInputClass, 'font-mono')}
            value={paymentIntentId}
            onChange={(e) => {
              setPaymentIntentId(e.target.value);
            }}
          />
        </Field>
        <Field label="Monto" htmlFor="refund-amount">
          <AmountInput
            id="refund-amount"
            className="h-10 text-sm"
            value={amount}
            onValueChange={setAmount}
          />
        </Field>
        <Button
          type="submit"
          variant="outline"
          className="h-10"
          loading={refund.isPending}
          disabled={!paymentIntentId.startsWith('pi_') || amount === '' || amount === '0'}
        >
          Devolver
        </Button>
      </form>
    </Panel>
  );
}

// ── Guía ──────────────────────────────────────────────────────────────────
function GuidePanel() {
  const guide = useQuery({ queryKey: queryKeys.guide, queryFn: api.getGuide });
  const router = useRouter();
  return (
    <Panel
      id="guia"
      className="scroll-mt-24 xl:col-span-2"
      title={
        <span className="flex items-center gap-2">
          <ListChecksIcon className="size-5 text-highlight" /> Guía del demo
        </span>
      }
      description="Guiones paso a paso con enlaces directos (03-demo/01-guion-demo.md)."
    >
      {guide.isError ? (
        <ErrorState error={guide.error} onRetry={() => void guide.refetch()} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {guide.data?.scripts.map((script) => (
            <section key={script.code} className="rounded-xl border border-border p-4">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold">
                <Badge variant="highlight">{script.code}</Badge> {script.title}
              </h3>
              <ol className="mt-3 flex list-decimal flex-col gap-2 pl-5 text-sm marker:text-muted-foreground">
                {script.steps.map((step, i) => (
                  <li key={i}>
                    {step.link ? (
                      step.link.startsWith('/') ? (
                        <a
                          href={step.link}
                          className="text-primary hover:underline"
                          onClick={(event) => {
                            event.preventDefault();
                            if (step.link) router.history.push(step.link);
                          }}
                        >
                          {step.text}
                        </a>
                      ) : (
                        <a
                          href={step.link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          {step.text} <ExternalLinkIcon className="size-3.5" />
                        </a>
                      )
                    ) : (
                      step.text
                    )}
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
}
